import { apiClient } from "./api";
import { Preferences } from "@capacitor/preferences";

export interface ConnectedDevice {
  id: string;
  name: string;
  serialNumber: string;
  dateAdded: string;
  pair_id: string;
}

export interface DeviceResponse {
  attached_devices: any[];
}

// Format raw device data from API to ConnectedDevice format
const formatDeviceData = (devices: any[]): ConnectedDevice[] => {
  return devices.map((device) => {
    const attachedDate = new Date(device.attached_at);
    const dateStr = attachedDate.toLocaleDateString("en-US", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });

    return {
      id: device.device_id,
      name:
        device.device_type === "biwaze_cough_system"
          ? "BiWaze® Cough"
          : "BiWaze® Clear",
      serialNumber: device.device_id,
      dateAdded: dateStr,
      pair_id: device.pair_id,
      type: device.device_type,
    };
  });
};

// Get all devices for a user
export const getUserDevices = async (
  profileId: number | string
): Promise<ConnectedDevice[]> => {
  try {
    const response = await apiClient.get<DeviceResponse>(`/devices`, {
      params: { profile_id: profileId },
    });

    const devicesData = response.data.attached_devices;
    const formattedDevices =
      devicesData.length > 0 ? formatDeviceData(devicesData) : [];

    // Store devices in Capacitor Preferences
    try {
      await Preferences.set({
        key: `connectedDevices_${profileId}`,
        value: JSON.stringify(formattedDevices),
      });
    } catch (error) {
      console.error("Failed to store devices in preferences:", error);
    }

    return formattedDevices;
  } catch (error) {
    console.error("Error fetching devices:", error);
    throw error;
  }
};

// Attach a new device
export const attachDevice = async (
  profileId: string | number,
  deviceType: string,
  deviceId: string,
  hash: string
): Promise<ConnectedDevice[]> => {
  try {
    const formattedDeviceType =
      deviceType === "cough" ? "biwaze_cough_system" : "biwaze_clear_system";

    const response = await apiClient.post<DeviceResponse>("/devices", {
      profile_id: profileId,
      device_type: formattedDeviceType,
      deviceId: deviceId,
      hash: hash,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    });

    return formatDeviceData(response.data.attached_devices);
  } catch (error: any) {
    // Standardized error handling
    if (error.response) {
      const status = error.response.status;
      const errorMessage =
        error.response.data?.message || "Unknown server error";

      if (status === 409 || errorMessage.includes("Invalid HOTP")) {
        throw new Error("Invalid HOTP");
      } else if (
        status === 410 ||
        errorMessage.includes("Device already attached")
      ) {
        throw new Error("Device already attached");
      } else {
        throw new Error(`Server error: ${errorMessage}`);
      }
    } else if (error.request) {
      throw new Error("No response from server. Please check your connection.");
    } else {
      throw error;
    }
  }
};

// Detach/disconnect a device
export const detachDevice = async (
  profileId: number,
  pairId: string
): Promise<ConnectedDevice[]> => {
  try {
    const response = await apiClient.delete<DeviceResponse>(`/devices/`, {
      data: {
        pair_id: pairId,
        profile_id: profileId,
      },
    });

    const devices = response.data.attached_devices;
    return devices.length > 0 ? formatDeviceData(devices) : [];
  } catch (error) {
    console.error("Error disconnecting device:", error);
    throw error;
  }
};

// Parse QR code data for device attachment
export const parseQRCodeData = (
  scannedString: string
): { deviceType: string; serialNumber: string; hotp: string } => {
  const parts = scannedString.split(",");

  if (parts.length !== 3) {
    throw new Error("Invalid QR code format");
  }

  const [deviceType, serialNumber, hotp] = parts;

  if (!deviceType || !serialNumber || !hotp) {
    throw new Error("Invalid QR code");
  }

  return { deviceType, serialNumber, hotp };
};
